import React, {useCallback} from 'react';
import {useNodeCallbackContext} from 'Contexts/NodeCallBackContext';
import {AstView, EnrichedAstNode} from 'rendition-core/types';
import {NodeLocation} from 'Contexts/HighlighterContext';
import {RenderFrameDiv} from 'Components/RenderFrameDiv';
import {NodeLocationInfo} from 'Contexts/NodeCallBackContext';

interface Props {
    scale: React.MutableRefObject<number>;
    workingNode: EnrichedAstNode;
    location: NodeLocation;
    view: AstView;
    horizontalOnly?: boolean;
    verticalOnly?: boolean;
}

export const GapHighlighter: React.FC<React.PropsWithChildren<Props>> = ({
    workingNode,
    location,
    scale,
    view,
    horizontalOnly,
    verticalOnly,
}) => {
    // get child sizes.
    const {nodeCallbacks} = useNodeCallbackContext();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, ...children] = [...workingNode.children].filter((child) => child.styles.position !== 'absolute');

    return (
        <>
            {children.map((child) => {
                return (
                    <SingleGapHighlight
                        scale={scale}
                        gapValue={workingNode.innerPosition.gap}
                        getChildLocation={nodeCallbacks[child.internalId]}
                        getParentLocation={nodeCallbacks[workingNode.internalId]}
                        flexDirection={workingNode.calculatedLayoutAbstraction.flexDirection || 'row'}
                        horizontalOnly={horizontalOnly}
                        verticalOnly={verticalOnly}
                    />
                );
            })}
        </>
    );
};

const SingleGapHighlight: React.FC<{
    getChildLocation: undefined | (() => NodeLocationInfo);
    getParentLocation: undefined | (() => NodeLocationInfo);
    flexDirection: 'row' | 'column';
    gapValue: number | string;
    scale: React.MutableRefObject<number>;
    horizontalOnly?: boolean;
    verticalOnly?: boolean;
}> = ({scale, getParentLocation, getChildLocation, flexDirection, gapValue, horizontalOnly, verticalOnly}) => {
    const position = useCallback(() => {
        if (!getChildLocation || !getParentLocation) {
            return {
                left: '0px',
                height: '0px',
                width: '0px',
            };
        }
        const childLocation = getChildLocation().actual;
        const parentLocation = getParentLocation().actual;

        const y = childLocation.y - parentLocation.y - +gapValue * scale.current;
        const x = childLocation.x - parentLocation.x - +gapValue * scale.current;

        if (flexDirection === 'column') {
            return {
                top: y + 'px',
                height: +gapValue * scale.current + 'px',
            };
        } else {
            return {
                left: x + 'px',
                width: +gapValue * scale.current + 'px',
            };
        }
    }, [getChildLocation, getParentLocation, flexDirection, gapValue, scale]);

    if (flexDirection === 'column' && horizontalOnly) return null;
    if (flexDirection === 'row' && verticalOnly) return null;

    return (
        <RenderFrameDiv
            onRenderFrame={position}
            className="absolute bg-blue-600 opacity-40"
            style={
                flexDirection === 'column'
                    ? {
                          left: 0,
                          width: '100%',
                      }
                    : {
                          top: 0,
                          height: '100%',
                      }
            }
        />
    );
};
